import { useNavigate } from 'react-router-dom';
import type { FormEvent } from 'react';
import { BrandLogo } from '@/components/shell/BrandLogo';

const inputClass =
  'appearance-none border border-line rounded-md px-3 py-2.5 text-[13.5px] text-primary bg-transparent outline-none transition-colors placeholder:text-faint hover:border-line-default focus:border-line-strong';

export function Login() {
  const navigate = useNavigate();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    navigate('/welcome');
  };

  return (
    <section className="min-h-screen grid grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] bg-page text-primary max-[880px]:grid-cols-1">
      <div className="flex flex-col px-[clamp(24px,6vw,72px)] py-8 min-h-screen">
        <div className="pt-2" />
        <div className="flex-1 flex flex-col justify-center max-w-[400px] w-full mx-auto py-8">
          <h1 className="text-[26px] font-medium tracking-[-0.02em] leading-[1.2] m-0 mb-1.5 text-primary">
            Bienvenido de vuelta
          </h1>
          <p className="text-[13px] text-tertiary m-0 mb-8 leading-[1.5]">
            Ingresa para continuar con tu copilot clínico.
          </p>

          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="login-email" className="text-[11px] text-muted font-medium tracking-[0.01em]">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                placeholder="dr.vasquez@optimal.health"
                autoComplete="username"
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between">
                <label htmlFor="login-pwd" className="text-[11px] text-muted font-medium tracking-[0.01em]">
                  Contraseña
                </label>
                <a href="#" className="text-xs text-tertiary transition-colors hover:text-primary">
                  ¿La olvidaste?
                </a>
              </div>
              <input
                id="login-pwd"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                className={inputClass}
              />
            </div>

            <label className="flex items-center gap-2 text-xs text-muted select-none cursor-pointer mt-1 hover:[&>input]:opacity-90">
              <input type="checkbox" defaultChecked className="w-[13px] h-[13px] opacity-70 cursor-pointer accent-muted" />
              <span>Recordar este dispositivo</span>
            </label>

            <button
              type="submit"
              className="block w-full bg-primary text-surface text-[13.5px] font-medium text-center px-4 py-[11px] border-0 rounded-md mt-2.5 cursor-pointer select-none transition-opacity hover:opacity-90 active:translate-y-px"
            >
              Iniciar sesión
            </button>
          </form>

          <p className="text-xs text-muted m-0 mt-7">
            ¿No tienes cuenta?{' '}
            <a href="#" className="text-xs text-tertiary border-b border-line-default pb-px transition-colors hover:text-primary hover:border-primary">
              Solicita acceso
            </a>
          </p>
        </div>

        <footer className="flex gap-2 items-center text-[11px] text-faint">
          <span>© Optimal Health</span>
          <span className="text-faint">·</span>
          <a href="#" className="text-tertiary transition-colors hover:text-primary">Privacidad</a>
          <span className="text-faint">·</span>
          <a href="#" className="text-tertiary transition-colors hover:text-primary">Términos</a>
        </footer>
      </div>

      <aside
        className="relative overflow-hidden p-[clamp(32px,5vw,64px)] text-[#F5F5F7] max-[880px]:hidden"
        style={{
          background:
            'radial-gradient(140% 100% at 50% 50%, rgba(20, 24, 36, 0.25) 0%, #050507 50%, #020203 100%)',
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(140,180,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(140,180,255,0.045) 1px, transparent 1px)',
            backgroundSize: '72px 72px',
            filter: 'blur(0.6px)',
            maskImage: 'radial-gradient(80% 70% at 50% 40%, #000 0%, transparent 90%)',
            WebkitMaskImage: 'radial-gradient(80% 70% at 50% 40%, #000 0%, transparent 90%)',
            animation: 'login-grid-drift 26s linear infinite',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-[30%] -left-[20%] w-[140%] h-[80%] pointer-events-none"
          style={{
            background:
              'radial-gradient(50% 60% at 50% 50%, rgba(56, 189, 248, 0.55) 0%, rgba(99, 102, 241, 0.35) 35%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-[30%] -right-[20%] w-[140%] h-[80%] pointer-events-none"
          style={{
            background:
              'radial-gradient(50% 60% at 50% 50%, rgba(125, 211, 252, 0.48) 0%, rgba(59, 130, 246, 0.30) 35%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 w-[860px] h-[860px] pointer-events-none -z-0"
          style={{
            background:
              'radial-gradient(circle, rgba(125, 211, 252, 0.28) 0%, rgba(99, 102, 241, 0.14) 35%, transparent 65%)',
            filter: 'blur(80px)',
            animation: 'login-pulse 5200ms cubic-bezier(0.45, 0, 0.55, 1) infinite',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 w-[420px] h-[420px] pointer-events-none -z-0"
          style={{
            background:
              'radial-gradient(circle, rgba(186, 230, 253, 0.28), rgba(125, 211, 252, 0.10) 50%, transparent 75%)',
            filter: 'blur(36px)',
            animation: 'login-pulse-soft 3800ms cubic-bezier(0.45, 0, 0.55, 1) infinite',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(80% 80% at 50% 45%, transparent 30%, rgba(0, 0, 0, 0.55) 90%)',
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#F5F5F7] pointer-events-none w-[55%] max-w-[504px]"
          aria-hidden="true"
        >
          <div
            className="welcome-rise"
            style={{ filter: 'drop-shadow(0 0 32px rgba(125, 211, 252, 0.35))' }}
          >
            <BrandLogo style={{ width: '100%' }} />
          </div>
        </div>
      </aside>
    </section>
  );
}
